import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { combineLatest, filter, Observable, of, switchMap, take } from 'rxjs';
import { ApplicationParameterDTO } from '../dtos/application-parameter.dto';
import { IStudy, StudyDTO } from '../dtos/study.dto';
import { IOrganization } from '../interfaces/organization.interface';
import { IUser } from '../model/user.model';
import { GetPlatformRolesOperationsAction } from '../store/operation/operation.actions';
import { OperationState } from '../store/operation/operation.state';
import { GetSelfOrganizationsAction } from '../store/organization/organization.actions';
import { OrganizationState } from '../store/organization/organization.state';
import { GetSidebarAction } from '../store/sidebar/sidebar.actions';
import { GetSelfStudiesAction } from '../store/study/study.actions';
import { StudyState } from '../store/study/study.state';
import { GetUserInfoAction } from '../store/user/user.actions';
import { UserState } from '../store/user/user.state';

@Injectable({
  providedIn: 'root',
})
export class UserResolver {
  @Select(UserState.getLoggedUser) public readonly loggedUser$: Observable<IUser>;
  @Select(OrganizationState.getSelfOrganizations) selfOrganizations$: Observable<IOrganization[]>;
  @Select(StudyState.getStudies) public readonly selfStudies$: Observable<StudyDTO[]>;
  @Select(OperationState.getApplicationParameters) public readonly applicationParameters$: Observable<
    ApplicationParameterDTO[]
  >;

  constructor(private readonly store: Store) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public resolve(route: ActivatedRouteSnapshot): Observable<any> {
    return of(true).pipe(
      switchMap(() => this.store.dispatch(new GetUserInfoAction())),
      switchMap(() => this.loggedUser$),
      filter((user) => !!user),
      switchMap((user) => {
        return combineLatest([of(user), this.selfOrganizations$]).pipe(take(1));
      }),
      switchMap(([user, selfOrganizations]: [IUser, IOrganization[]]) => {
        if (!selfOrganizations || selfOrganizations.length == 0) {
          return this.store.dispatch(new GetSelfOrganizationsAction()).pipe(switchMap(() => of(user)));
        }
        return of(user);
      }),
      switchMap(() => combineLatest([this.loggedUser$, this.selfStudies$]).pipe(take(1))),
      switchMap(([user, selfStudies]: [IUser, IStudy[]]) => {
        if (!user.isSuper() && !user.isClientAdmin() && (!selfStudies || selfStudies.length == 0)) {
          return this.store.dispatch(new GetSelfStudiesAction()).pipe(switchMap(() => of(true)));
        }
        return of(true);
      }),
      switchMap(() => this.applicationParameters$.pipe(take(1))),
      switchMap((appParams: ApplicationParameterDTO[]) => {
        if (!appParams || appParams.length == 0) {
          return this.store.dispatch(new GetPlatformRolesOperationsAction()).pipe(switchMap(() => of(true)));
        }
        return of(true);
      }),
      // Load the navigation sidebar. The legacy IdentityServer4 flow dispatched
      // GetSidebarAction eagerly on login (auth.state), but that path was removed
      // with the Keycloak migration; without it the sidebar stays empty and the
      // landing HomeComponent shows "No studies are configured" for every user.
      switchMap(() => this.store.dispatch(new GetSidebarAction()).pipe(switchMap(() => of(true)))),
    );
  }
}
